/** @jsxImportSource @emotion/react */
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Tooltip,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { ContactApi } from "../../api/Contacts";
import { ContactsAtom } from "../../atoms/ContactsAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { ContactStatus, IContactTag } from "../../generated/Api";
import { notSelect } from "../../styles/general";
import { IContact } from "../../types/Contact";
import StyledButton from "../StyledButton";
import StyledCheckBox from "../StyledCheckBox";
import StyledInput from "../StyledInput";

export const IconButton = styled.div`
  width: 32px;
  height: 32px;

  padding: 8px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #fff2e5;

  border-radius: 4px;

  color: #fb972e;

  transition: all 0.3s ease-in-out;
  cursor: pointer;

  svg {
    max-width: 100%;
    max-height: 100%;
  }

  &:hover {
    background: #fb972e;

    color: white;
  }
`;

export const FormContainer = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  align-items: center;
  justify-content: center;
  grid-gap: 24px;
`;

export const FormWrapper = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  grid-gap: 8px;

  > label {
    font-size: 16px;
    font-weight: 600;
    color: #383838;
  }

  .react-tel-input .form-control {
    width: 100%;
    height: 40px;
    border: 1px solid #dddddd;
    border-radius: 5px;
    outline: none;
    font-size: 15px;
    color: #383838;

    :focus {
      box-shadow: 0px 0px 6px #fb972e80;
      border: 1px solid #fb972e;
    }
  }

  .react-tel-input .selected-flag:focus:before,
  .react-tel-input .selected-flag.open:before {
    box-shadow: none;
    border: none;
    outline: none;
  }

  input {
    width: 100% !important;
  }
`;

export const PopoverHeaderCSS = css`
  height: 56px;

  padding: 0 32px;

  display: flex;
  align-items: center;
  justify-content: start;

  font-size: 18px;
  font-weight: 600;
  color: #333333;
`;

export const PopoverBodyCSS = css`
  padding: 16px 32px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  grid-gap: 16px;
`;

export const PopoverFooterCSS = css`
  padding: 16px 32px 32px;

  display: flex;
  align-items: center;
  justify-content: end;
  grid-gap: 12px;

  border-top: none;

  #styled-button {
    width: 72px;

    border-radius: 4px;
  }
`;

const EditActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const FormLabel = styled.div`
  color: #333;
  font: normal normal 600 16px/26px Source Sans Pro;
  margin-bottom: 8px;
`;

const EmailConfirmation = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  grid-gap: 4px;

  margin-top: 16px;
`;

const ConfirmationText = styled.div`
  font-size: 15px;
  color: #707070;
  font-weight: 400;
`;

interface Props {
  contactToEdit?: {
    name: string;
    firstName?: string;
    lastName?: string;
    email: string;
    _id: string;
    userUid: string;
    labels: string[];
    tags: IContactTag[];
    avatar: string;
    status: ContactStatus;
    createdAt: Date;
    phone?: string;
  };
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export default function EditEmailPopOver(props: Props): JSX.Element {
  const { onClose, contactToEdit, onOpen, isOpen } = props;
  const [editedEmail, setEditedEmail] = useState("");
  const [editedFirstName, setEditedFirstName] = useState("");
  const [editedLastName, setEditedLastName] = useState("");
  const [editedMobile, setEditedMobile] = useState("");
  const user = useRecoilValue(UserAtom);
  let setContacts = useSetRecoilState(ContactsAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [isEditEmailEmpty, setIsEditEmailEmpty] = useState(false);

  useEffect(() => {
    if (!contactToEdit) return;
    setEditedEmail(contactToEdit.email);
    setEditedFirstName(contactToEdit.firstName);
    setEditedLastName(contactToEdit.lastName);
    setEditedMobile(contactToEdit.phone);
  }, []);

  async function handleUpdateContact(
    email: string,
    contact: IContact,
    firstName?: string,
    lastName?: string,
    phone?: string
  ) {
    setIsLoading(true);
    try {
      if (!email || !user) return;
      const updatedContact = { ...contact, email, firstName, lastName, phone };
      await ContactApi.update(updatedContact, contact._id);
      setContacts((contacts) =>
        contacts
          ? contacts?.map((c) =>
              c._id === updatedContact._id ? updatedContact : c
            )
          : []
      );
      if (sendConfirmation) {
        const response = await ContactApi.sendConfirmation({
          contactIds: [contact._id],
        });
        if (response) {
          setContacts(
            (contacts) =>
              contacts &&
              contacts.map((c) => ({
                ...c,
                ...(c._id === contact._id && {
                  status: ContactStatus.PENDING_CONFIRMATION,
                }),
              }))
          );

          toast("Email confirmation sent", { type: "success" });
        }
      }

      editedEmail !== contactToEdit.email &&
        toast("Contact email updated succesfully", { type: "success" });
      onClose();
    } catch (err) {
      if (err.message === "CONTACT_ALREADY_EXISTS") {
        toast("A contact with this email address already exists", {
          type: "warning",
        });
      }
    }
    setIsLoading(false);
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleUpdateContact(
          editedEmail,
          contactToEdit,
          editedFirstName,
          editedLastName,
          editedMobile
        );
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [editedEmail, sendConfirmation]);

  return (
    <Popover
      closeOnEsc
      closeOnBlur
      placement="right-start"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Tooltip hasArrow placement="top" label="Edit">
        <PopoverTrigger>
          <IconButton onClick={onOpen}>
            <FaPencilAlt />
          </IconButton>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent width="400px">
        <PopoverArrow />
        <PopoverHeader css={PopoverHeaderCSS}>Quick Edit</PopoverHeader>
        <PopoverCloseButton
          background={"none"}
          border={"none"}
          cursor={"pointer"}
          top="1rem"
          right="32px"
        />
        <PopoverBody css={PopoverBodyCSS}>
          <FormContainer>
            <FormWrapper>
              <label>First Name</label>
              <StyledInput
                value={editedFirstName}
                onChange={(e) => {
                  setEditedFirstName(e.target.value);
                }}
                placeholder="Enter first name"
              />
            </FormWrapper>

            <FormWrapper>
              <label>Last Name</label>
              <StyledInput
                value={editedLastName}
                onChange={(e) => {
                  setEditedLastName(e.target.value);
                }}
                placeholder="Enter last name"
              />
            </FormWrapper>
          </FormContainer>

          <FormWrapper>
            <label>Mobile</label>
            <PhoneInput
              country={"us"}
              value={editedMobile}
              onChange={(value) => {
                setEditedMobile(value);
              }}
              placeholder={"(917) 555-5555"}
            />
          </FormWrapper>

          <FormWrapper>
            <label>Email Address</label>
            <StyledInput
              value={editedEmail}
              onChange={(e) => {
                setEditedEmail(e.target.value);
                if (!e.target.value.length) {
                  setIsEditEmailEmpty(true);
                } else setIsEditEmailEmpty(false);
              }}
              placeholder="Enter email address"
            />
          </FormWrapper>

          <EmailConfirmation
            onClick={() => setSendConfirmation(!sendConfirmation)}
          >
            <StyledCheckBox
              check={sendConfirmation}
              handleCheck={() => setSendConfirmation(!sendConfirmation)}
              customCss={notSelect}
            />
            <ConfirmationText>
              Send email confirmation to contact
            </ConfirmationText>
          </EmailConfirmation>
        </PopoverBody>
        <PopoverFooter css={PopoverFooterCSS}>
          <StyledButton inverted onClick={() => onClose()}>
            Cancel
          </StyledButton>
          <StyledButton
            onClick={() =>
              handleUpdateContact(
                editedEmail,
                contactToEdit,
                editedFirstName,
                editedLastName,
                editedMobile
              )
            }
            isLoading={isLoading}
            disable={isEditEmailEmpty}
          >
            Save
          </StyledButton>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
